/**
 * Levertijd van een artikel voor Google Merchant Center: welke beheerde tekst hoort erbij, en welke
 * verwerkingstijd (handling time) zet je daarvoor in de feed.
 *
 * Waarom: Merchant Center liet voor álle artikelen "1-4 werkdagen" zien (één vlak verzendbeleid),
 * terwijl de site bij een artikel op bestelling "1-3 weken" belooft. Google vergelijkt die belofte
 * met wat klanten ervaren; een te snelle belofte kost vertrouwen en uiteindelijk zichtbaarheid.
 * Nu krijgt elk artikel zijn eigen verwerkingstijd, uit dezelfde tekst die op de productpagina staat.
 *
 * De totale levertijd = verwerkingstijd (per artikel, hier) + bezorgduur (accountbeleid in Merchant
 * Center en per regel in g:shipping, NL 1-2 werkdagen). Vandaar het aftrekken van FEED_BEZORGDUUR.
 */
export interface ArtikelLevertijden {
  artikelOpVoorraad: string;
  artikelOpBestelling: string;
  artikelEigenTransport?: string;
}

/**
 * Bezorgduur (transit) in werkdagen per land. Staat per regel in g:shipping: zodra een feedregel eigen
 * verzendkosten heeft, negeert Merchant Center het accountbeleid voor dat product — ook de bezorgduur.
 * NL is de basis voor de verwerkingstijd hieronder.
 */
export const FEED_BEZORGDUUR_PER_LAND = { NL: { min: 1, max: 2 }, BE: { min: 1, max: 3 } } as const;
export const FEED_BEZORGDUUR = FEED_BEZORGDUUR_PER_LAND.NL;

/** Zelfde keuze als de productpagina (ProductPage.leadTimeFor): eigen transport → op bestelling → voorraad. */
export function artikelLevertijdTekst(
  inStock: boolean | undefined,
  eigenTransport: boolean,
  lt: ArtikelLevertijden,
): string {
  if (eigenTransport) return lt.artikelEigenTransport || lt.artikelOpBestelling;
  return inStock === false ? lt.artikelOpBestelling : lt.artikelOpVoorraad;
}

/** "2-3 werkdagen" → 2..3, "1-3 weken" → 5..15 werkdagen. Maanden of onleesbaar → null. */
export function levertijdWerkdagen(tekst: string): { min: number; max: number } | null {
  const nums = (tekst.match(/\d+/g) ?? []).map(Number).filter((n) => n > 0 && n < 400);
  if (nums.length === 0) return null;
  if (/maand|month/i.test(tekst)) return null;
  const factor = /\bweken?\b|\bweeks?\b/i.test(tekst) ? 5 : 1;
  const a = nums[0]! * factor;
  const b = (nums[1] ?? nums[0]!) * factor;
  return { min: Math.min(a, b), max: Math.max(a, b) };
}

/** Verwerkingstijd voor g:min_handling_time / g:max_handling_time, of null (dan geldt het accountbeleid). */
export function feedVerwerkingstijd(tekst: string): { min: number; max: number } | null {
  const totaal = levertijdWerkdagen(tekst);
  if (!totaal) return null;
  const min = Math.max(0, totaal.min - FEED_BEZORGDUUR.min);
  const max = Math.max(min, totaal.max - FEED_BEZORGDUUR.max);
  return { min, max };
}
